import { Divider } from "$root/components/Divider";
import { SidePanelContent } from "$root/components/common/SidePanelV2";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { PortfolioLabContext, type PortfolioLabChartItem } from "$root/functional-areas/portfolio-lab/ctx";
import { useLabSession } from "$root/functional-areas/portfolio-lab/hooks/useLabSession";
import { SidePanelListItem } from "$root/functional-areas/workspace/ui/SidePanelItem";
import { DraggableSettingsPanel } from "$root/ui-lib/DraggableSettingsPanel";
import { Column, Row, Text } from "@mdotm/shadcn/legacy/components";
import { useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { LabChartTypeEnum } from "@eu.mdotm.api/portfolio-lab";
import { PerformanceChartSettings } from "./charts/Performance/PerformanceChartSettings";
import { PortfolioMetricsSettings } from "./charts/PortfolioMetrics/PortfolioMetricsSettings";

const CHART_LABELS: Record<LabChartTypeEnum, string> = {
	PERFORMANCE: "Performance Chart",
	EXPOSURE: "Exposure Grid",
	TABLE: "Portfolio Metrics",
};

const CHART_DESCRIPTIONS: Record<LabChartTypeEnum, string> = {
	PERFORMANCE: "Series and historical scenarios",
	EXPOSURE: "Asset class breakdown",
	TABLE: "Risk and return statistics",
};

/** Resolve the drill-down settings panel for a chart, binding chart-scoped props (e.g. the TABLE uuid). */
function getChartSettingsPanel(c: PortfolioLabChartItem): React.ComponentType | null {
	switch (c.type) {
		case "PERFORMANCE":
			return PerformanceChartSettings;
		case "TABLE":
			return () => <PortfolioMetricsSettings chartUuid={c.uuid} />;
		default:
			return null;
	}
}

export function PortfolioLabSettings() {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const ctx = useMandatoryContext(PortfolioLabContext);
	const charts = useAtomValue(ctx.charts$);
	const { saveLabSession } = useLabSession();

	function togglePanel(type: LabChartTypeEnum, visible: boolean) {
		writeAtom(ctx.charts$, (cs) => cs.map((c) => (c.type === type ? { ...c, visible } : c)));
		saveLabSession();
	}

	function reorderPanels(newCharts: PortfolioLabChartItem[]) {
		writeAtom(ctx.charts$, () => newCharts);
		saveLabSession();
	}

	return (
		<DraggableSettingsPanel
			title="Display settings"
			description="Show, hide and reorder panels"
			items={charts}
			itemKey={(c) => c.uuid}
			onReorder={reorderPanels}
			searchExtract={(c) => CHART_LABELS[c.type]}
			emptyState={
				<SidePanelContent>
					<Column className="items-center justify-center h-full">
						<Text type="Body/L/Book">No panels available.</Text>
					</Column>
				</SidePanelContent>
			}
		>
			{(c, { dragIcon, isDragging }) => {
				const settingsPanel = getChartSettingsPanel(c);
				return (
					<Column className="overflow-hidden">
						<Row gap={8} alignItems="center">
							{dragIcon}
							<SidePanelListItem
								title={CHART_LABELS[c.type]}
								description={CHART_DESCRIPTIONS[c.type]}
								checked={c.visible}
								onToggle={(visible) => togglePanel(c.type, visible)}
								{...(settingsPanel
									? { onDrillDown: () => sidePanelCtx.push(settingsPanel) }
									: { onDrillDown: undefined })}
							/>
						</Row>
						{!isDragging && <Divider className="border-dashed" />}
					</Column>
				);
			}}
		</DraggableSettingsPanel>
	);
}
